import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { getErrorMessage } from '../utils/errorUtils';
import { formatRut, CHILEAN_COMMUNES } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Building2,
  Plus,
  Search,
  Loader2,
  Phone,
  Mail,
  Edit,
} from 'lucide-react';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    rut: '',
    business_name: '',
    trade_name: '',
    giro: '',
    address: '',
    comuna: '',
    phone: '',
    email: '',
    contact_name: '',
    payment_terms: '30 días',
  });

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/suppliers', {
        params: searchTerm ? { search: searchTerm } : {}
      });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.rut || !formData.business_name || !formData.giro || !formData.email) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/suppliers', formData);
      toast.success('Proveedor registrado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al registrar proveedor'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      rut: '',
      business_name: '',
      trade_name: '',
      giro: '',
      address: '',
      comuna: '',
      phone: '',
      email: '',
      contact_name: '',
      payment_terms: '30 días',
    });
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Proveedores
          </h1>
          <p className="text-slate-500 mt-1">
            Gestión de proveedores de bienes y servicios
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-600/20" data-testid="add-supplier-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Proveedor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Registrar Nuevo Proveedor</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>RUT *</Label>
                  <Input
                    name="rut"
                    value={formData.rut}
                    onChange={handleInputChange}
                    placeholder="76.123.456-7"
                    className="bg-slate-50"
                    data-testid="supplier-rut"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Razón Social *</Label>
                  <Input
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    placeholder="Empresa S.A."
                    className="bg-slate-50"
                    data-testid="supplier-business-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre de Fantasía</Label>
                  <Input
                    name="trade_name"
                    value={formData.trade_name}
                    onChange={handleInputChange}
                    placeholder="Nombre comercial"
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Giro *</Label>
                  <Input
                    name="giro"
                    value={formData.giro}
                    onChange={handleInputChange}
                    placeholder="Actividad comercial"
                    className="bg-slate-50"
                    data-testid="supplier-giro"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Dirección</Label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Dirección completa"
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Comuna</Label>
                  <Select 
                    value={formData.comuna} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, comuna: v }))}
                  >
                    <SelectTrigger className="bg-slate-50">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {CHILEAN_COMMUNES.map(comuna => (
                        <SelectItem key={comuna} value={comuna}>{comuna}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+56 2 1234 5678"
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="contacto@empresa.cl"
                    className="bg-slate-50"
                    data-testid="supplier-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre de Contacto</Label>
                  <Input
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    placeholder="Persona de contacto"
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Condiciones de Pago</Label>
                  <Select 
                    value={formData.payment_terms} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, payment_terms: v }))}
                  >
                    <SelectTrigger className="bg-slate-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Contado">Contado</SelectItem>
                      <SelectItem value="15 días">15 días</SelectItem>
                      <SelectItem value="30 días">30 días</SelectItem>
                      <SelectItem value="45 días">45 días</SelectItem>
                      <SelectItem value="60 días">60 días</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700"
                  disabled={submitting}
                  data-testid="submit-supplier-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar Proveedor'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por razón social o RUT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-50"
              data-testid="search-suppliers"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : suppliers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Building2 className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No hay proveedores</p>
              <p className="text-sm">Agregue el primer proveedor para comenzar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>RUT</TableHead>
                    <TableHead>Razón Social</TableHead>
                    <TableHead className="hidden md:table-cell">Giro</TableHead>
                    <TableHead className="hidden lg:table-cell">Contacto</TableHead>
                    <TableHead className="hidden lg:table-cell">Condiciones</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map((supplier) => (
                    <TableRow key={supplier.supplier_id} className="table-row-hover">
                      <TableCell className="font-mono text-sm">
                        {formatRut(supplier.rut)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{supplier.business_name}</p>
                          {supplier.trade_name && (
                            <p className="text-xs text-slate-500">{supplier.trade_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{supplier.giro}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="space-y-1">
                          {supplier.contact_name && (
                            <p className="text-sm">{supplier.contact_name}</p>
                          )}
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Mail className="w-3 h-3" />
                            {supplier.email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">{supplier.payment_terms}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Activo
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Suppliers;
